/*********************************************************************************
 1. Dependencies
 *********************************************************************************/

import React from 'react';
import Helmet from 'react-helmet';
import BackgroundImage from '../../images/terrace-5.jpg';
import { LargeText } from '../atoms/StyledTypography';
import FeatureSearchBasicImage from '../../images/feature-search-basic.png';
import FeatureSearchBasic2xImage from '../../images/feature-search-basic@2x.png';
import FeatureSearchAdvancedImage from '../../images/feature-search-advanced.png';
import FeatureSearchAdvanced2xImage from '../../images/feature-search-advanced@2x.png';
import FeatureFilterDateImage from '../../images/feature-filter-date.png';
import FeatureFilterDate2xImage from '../../images/feature-filter-date@2x.png';
import FeatureFilterPriceImage from '../../images/feature-filter-price.png';
import FeatureFilterPrice2xImage from '../../images/feature-filter-price@2x.png';
import FeatureExportImage from '../../images/feature-export.png';
import FeatureExport2xImage from '../../images/feature-export@2x.png';
import FeatureCompareImage from '../../images/feature-compare.png';
import FeatureCompare2xImage from '../../images/feature-compare@2x.png';
import FeatureInsightsImage from '../../images/feature-insights.png';
import FeatureInsights2xImage from '../../images/feature-insights@2x.png';
import { PictureHybridPage } from '../templates/PictureHybridPage';
import { StyledContentWrapper } from '../atoms/StyledContentWrapper';
import { StyledContent } from '../atoms/StyledContent';
import { StyledMainPictureHybridPage } from '../atoms/StyledMainPictureHybridPage';
import { StyledIconLinkContainer } from '../atoms/StyledIconLinkContainer';
import { IconLink } from '../molecules/IconLink';
import { IconChartAnalytics } from '../atoms/IconChartAnalytics';
import { IconFilter } from '../atoms/IconFilter';
import { IconCloudDownload } from '../atoms/IconCloudDownload';
import { IconOscilloscope } from '../atoms/IconOscilloscope';
import { IconIdeas } from '../atoms/IconIdeas';
import { Colours } from '../../styles/variables';
import { StyledVerticalFlow } from '../atoms/StyledVerticalFlow';
import { Heading1, LargeResponsiveHeading, RegularText } from '../atoms/StyledTypography';
import { StyledPictureWithText } from '../atoms/StyledPictureWithText';
import { StyledFeaturePicture } from '../atoms/StyledFeaturePicture';
import { AnimatedScrollDown } from '../molecules/AnimatedScrollDown';


/*********************************************************************************
 2. Component
 *********************************************************************************/

export const Home = () => (
    <PictureHybridPage>
        <Helmet title="Where property insights surface | Price Paid Data"/>
        <StyledMainPictureHybridPage background={BackgroundImage}>
            <div>
                <StyledVerticalFlow align="center">
                    <LargeResponsiveHeading>Opening the doors to property data insights in England and
                        Wales.</LargeResponsiveHeading>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <LargeText>Search and filter through land registry sold house prices from 1995
                        to {(new Date().getFullYear())}</LargeText>
                </StyledVerticalFlow>
                <StyledIconLinkContainer>
                    <IconLink icon={<IconChartAnalytics width="48px" height="48px"/>}
                              href="#feature-search">Search</IconLink>
                    <IconLink icon={<IconFilter width="48px" height="48px"/>}
                              href="#feature-filter">Filter</IconLink>
                    <IconLink icon={<IconCloudDownload width="48px" height="48px"/>}
                              href="#feature-export">Export</IconLink>
                    <IconLink icon={<IconOscilloscope width="48px" height="48px"/>}
                              href="#feature-compare">Compare</IconLink>
                    <IconLink icon={<IconIdeas width="48px" height="48px"/>}
                              href="#feature-insights">Insights</IconLink>
                </StyledIconLinkContainer>
            </div>
            <AnimatedScrollDown/>
        </StyledMainPictureHybridPage>
        <StyledContentWrapper>
            <StyledContent>
                <StyledVerticalFlow align="center">
                    <IconLink name="feature-search" icon={<IconChartAnalytics width="36px" height="36px"/>}
                              iconColour={Colours.Carnation} textColour={Colours.Alto}
                              margin="0">Search</IconLink>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <Heading1>Search up-to-date property data from HM Land Registry</Heading1>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <StyledPictureWithText>
                        <RegularText>Find the exact properties you need with a flexible search engine that
                            provides
                            instant results.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureSearchBasicImage} srcSet={`${FeatureSearchBasic2xImage} 2x`}
                                 alt="Basic search"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                    <StyledPictureWithText direction="row-reverse">
                        <RegularText>You can do simple searches for specific postcodes, or more advanced postcode
                            patterns and even postcode groups.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureSearchAdvancedImage} srcSet={`${FeatureSearchAdvanced2xImage} 2x`}
                                 alt="Advanced search"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <IconLink name="feature-filter" icon={<IconFilter width="36px" height="36px"/>}
                              iconColour={Colours.Carnation} textColour={Colours.Alto}
                              margin="0">Filter</IconLink>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <Heading1>Make use of all the filters and drill-down to specific data</Heading1>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <StyledPictureWithText>
                        <RegularText>With filters you can narrow down the results and create an even more
                            specific result
                            set. The date filter lets you define a date ranges and as a result you'll be able to
                            filter out
                            properties that were sold on a specific date, month or year.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureFilterDateImage} srcSet={`${FeatureFilterDate2xImage} 2x`}
                                 alt="Filter by date"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                    <StyledPictureWithText direction="row-reverse">
                        <RegularText>The price filter allows you to set property price boundaries. You can set a
                            minimum
                            price, a maximum price or both. You can also define whether all property sales
                            should be within
                            the boundaries set or not.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureFilterPriceImage} srcSet={`${FeatureFilterPrice2xImage} 2x`}
                                 alt="Filter by price"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <IconLink name="feature-export" icon={<IconCloudDownload width="36px" height="36px"/>}
                              iconColour={Colours.Carnation} textColour={Colours.Alto}
                              margin="0">Export</IconLink>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <Heading1>Export results sets</Heading1>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <StyledPictureWithText>
                        <RegularText>The flexible built-in export functionality allows you extract result from
                            Price Paid
                            Data into industry standard CSV files. You can also customise the data exported by
                            selecting
                            which columns to extract and what the column names should be.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureExportImage} srcSet={`${FeatureExport2xImage} 2x`}
                                 alt="Export data"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <div name="feature-compare">
                        <IconLink icon={<IconOscilloscope width="36px" height="36px"/>}
                                  iconColour={Colours.Carnation} textColour={Colours.Alto}
                                  margin="0">Compare</IconLink>
                    </div>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <Heading1>Compare everything - properties, streets, cities</Heading1>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <StyledPictureWithText direction="row-reverse">
                        <RegularText>Whether you want to compare data on properties, streets or even cities, you
                            can do it
                            with ease. You'll be able to compare properties to see how they are performing
                            against each
                            other, see which street has the biggest value and even which cities has had the most
                            sales.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureCompareImage} srcSet={`${FeatureCompare2xImage} 2x`}
                                 alt="Compare data"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <IconLink name="feature-insights" icon={<IconIdeas width="36px" height="36px"/>}
                              iconColour={Colours.Carnation} textColour={Colours.Alto}
                              margin="0">Insights</IconLink>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <Heading1>A transparent property market</Heading1>
                </StyledVerticalFlow>
                <StyledVerticalFlow align="center">
                    <StyledPictureWithText>
                        <RegularText>Define your parameters and retrieve the stats and aggregations you need.
                            From country
                            wide stats, to specific postcodes or regions or even cities.</RegularText>
                        <StyledFeaturePicture>
                            <img src={FeatureInsightsImage} srcSet={`${FeatureInsights2xImage} 2x`}
                                 alt="Property data insights"/>
                        </StyledFeaturePicture>
                    </StyledPictureWithText>
                </StyledVerticalFlow>
            </StyledContent>
        </StyledContentWrapper>
    </PictureHybridPage>
);



// WEBPACK FOOTER //
// ./src/components/pages/Home.js